import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chart, registerables } from 'chart.js';
import { req } from '../services/api';

Chart.register(...registerables);

const money = (value) => Number(value || 0).toFixed(0) + ' €';

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const chartRidesRef = useRef(null);
  const chartRevenueRef = useRef(null);
  const chartRidesInstance = useRef(null);
  const chartRevenueInstance = useRef(null);

  useEffect(() => {
    loadStats();

    return () => {
      if (chartRidesInstance.current) chartRidesInstance.current.destroy();
      if (chartRevenueInstance.current) chartRevenueInstance.current.destroy();
    };
  }, []);

  const loadStats = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await req('GET', '/stats');
      setStats(data);
    } catch (error) {
      console.error('Dashboard stats load failed:', error);
      setStats(null);
      setError(
        error?.message ||
          'Nepoda\u0159ilo se na\u010d\u00edst data p\u0159ehledu.'
      );
    } finally {
      setLoading(false);
    }
  };

  const loadCharts = async () => {
    try {
      const data = await req('GET', '/stats/charts');
      if (!data?.ridesPerDay) return;

      const labels = data.ridesPerDay.map(d => d.date);
      const rideCounts = data.ridesPerDay.map(d => d.count);
      const revenues = data.ridesPerDay.map(d => d.revenue);

      const commonOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false }
        },
        scales: {
          x: {
            ticks: { color: '#888', font: { size: 11 } },
            grid: { color: 'rgba(255,255,255,0.05)' }
          },
          y: {
            ticks: { color: '#888', font: { size: 11 } },
            grid: { color: 'rgba(255,255,255,0.05)' },
            beginAtZero: true
          }
        }
      };

      if (chartRidesInstance.current) chartRidesInstance.current.destroy();
      if (chartRevenueInstance.current) chartRevenueInstance.current.destroy();

      chartRidesInstance.current = new Chart(chartRidesRef.current, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            data: rideCounts,
            backgroundColor: 'rgba(200,168,75,0.5)',
            borderColor: '#C8A84B',
            borderWidth: 2,
            borderRadius: 6
          }]
        },
        options: commonOptions
      });

      chartRevenueInstance.current = new Chart(chartRevenueRef.current, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            data: revenues,
            borderColor: '#4CAF7D',
            backgroundColor: 'rgba(76,175,125,0.1)',
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointBackgroundColor: '#4CAF7D'
          }]
        },
        options: commonOptions
      });
    } catch (error) {
      console.error('Dashboard charts load failed:', error);
    }
  };

  useEffect(() => {
    if (
      !loading &&
      stats &&
      chartRidesRef.current &&
      chartRevenueRef.current
    ) {
      loadCharts();
    }
  }, [loading, stats]);

  const totalApprovalItems = stats ? (
    (stats.pendingIndependentDrivers || 0) +
    (stats.pendingFleetDrivers || 0) +
    (stats.pendingFleets || 0) +
    (stats.pendingDriverDocs || 0) +
    (stats.pendingFleetDriverDocs || 0) +
    (stats.pendingFleetDocs || 0) +
    (stats.pendingVehicles || 0)
  ) : 0;

  const totalWallets = stats
    ? Number(stats.driverWalletBalance || 0) + Number(stats.fleetWalletBalance || 0)
    : 0;

  const pendingPayouts = stats
    ? Number(stats.pendingDriverPayouts || 0) + Number(stats.pendingFleetPayouts || 0)
    : 0;

  const ApprovalRow = ({ label, value, path }) => (
    <button
      className="dashboard-action-row"
      onClick={() => navigate(path)}
      type="button"
    >
      <span>{label}</span>
      <span className={value > 0 ? 'dashboard-action-count alert' : 'dashboard-action-count'}>
        {value}
      </span>
    </button>
  );

  if (loading) {
    return (
      <div>
        <div className="page-title">Přehled</div>
        <div className="page-sub">Řídicí centrum Euro.taxi</div>
        <div className="loading-cell">Načítám data...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div>
        <div className="page-title">P\u0159ehled</div>
        <div className="page-sub">\u0158\u00eddic\u00ed centrum Euro.taxi</div>
        <div className="loading-cell">
          {error || 'Nepoda\u0159ilo se na\u010d\u00edst data p\u0159ehledu.'}
        </div>
        <button className="btn-sm btn-gold" onClick={loadStats}>
          Zkusit znovu
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header-row">
        <div>
          <div className="page-title">Přehled</div>
          <div className="page-sub">Řídicí centrum Euro.taxi</div>
        </div>

        <div className="dashboard-header-actions">
          <button className="btn-sm btn-gold" onClick={() => navigate('/rides')}>
            Otevřít jízdy
          </button>
          <button className="btn-sm btn-green" onClick={() => navigate('/payouts')}>
            Finance
          </button>
        </div>
      </div>

      <div className="dashboard-section-title">Vyžaduje pozornost</div>

      <div className="stats-grid dashboard-top-stats">
        <div className={totalApprovalItems > 0 ? 'stat-card alert' : 'stat-card'}>
          <div className="stat-value">{totalApprovalItems}</div>
          <div className="stat-label">Čekající kontroly</div>
        </div>

        <div className={(stats.activeRides || 0) > 0 ? 'stat-card active' : 'stat-card'}>
          <div className="stat-value">{stats.activeRides || 0}</div>
          <div className="stat-label">Aktivní jízdy</div>
        </div>

        <div className={pendingPayouts > 0 ? 'stat-card alert-gold' : 'stat-card'}>
          <div className="stat-value">{pendingPayouts}</div>
          <div className="stat-label">Výplaty k řešení</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{money(stats.revenue)}</div>
          <div className="stat-label">Tržby z dokončených jízd</div>
        </div>
      </div>

      <div className="dashboard-grid-two">
        <div className="card dashboard-panel">
          <div className="card-header">
            <span className="card-title">Schválení a dokumenty</span>
            <span className="dashboard-muted">{totalApprovalItems} položek</span>
          </div>

          <div className="dashboard-action-list">
            <ApprovalRow
              label="Samostatní řidiči – účty"
              value={stats.pendingIndependentDrivers || 0}
              path="/drivers"
            />
            <ApprovalRow
              label="Samostatní řidiči – dokumenty"
              value={stats.pendingDriverDocs || 0}
              path="/drivers"
            />
            <ApprovalRow
              label="Flotily – účty"
              value={stats.pendingFleets || 0}
              path="/fleets"
            />
            <ApprovalRow
              label="Flotily – dokumenty"
              value={stats.pendingFleetDocs || 0}
              path="/fleets"
            />
            <ApprovalRow
              label="Řidiči flotil – účty"
              value={stats.pendingFleetDrivers || 0}
              path="/fleet-drivers"
            />
            <ApprovalRow
              label="Řidiči flotil – dokumenty"
              value={stats.pendingFleetDriverDocs || 0}
              path="/fleet-drivers"
            />
            <ApprovalRow
              label="Vozidla"
              value={stats.pendingVehicles || 0}
              path="/vehicles"
            />
          </div>
        </div>

        <div className="card dashboard-panel">
          <div className="card-header">
            <span className="card-title">Provoz</span>
            <button className="dashboard-link-btn" onClick={() => navigate('/rides')}>
              Zobrazit jízdy
            </button>
          </div>

          <div className="dashboard-metric-list">
            <div className="dashboard-metric-row">
              <span>Aktivní</span>
              <strong>{stats.activeRides || 0}</strong>
            </div>
            <div className="dashboard-metric-row">
              <span>Dokončené</span>
              <strong>{stats.completedRides || 0}</strong>
            </div>
            <div className="dashboard-metric-row">
              <span>Zrušené</span>
              <strong>{stats.cancelledRides || 0}</strong>
            </div>
            <div className="dashboard-metric-row total">
              <span>Celkem jízd</span>
              <strong>{stats.rides || 0}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-section-title">Platforma</div>

      <div className="stats-grid">
        <div className="stat-card clickable" onClick={() => navigate('/customers')}>
          <div className="stat-value">{stats.customers || 0}</div>
          <div className="stat-label">Zákazníci</div>
        </div>

        <div className="stat-card clickable" onClick={() => navigate('/drivers')}>
          <div className="stat-value">{stats.independentDrivers || 0}</div>
          <div className="stat-label">Řidiči</div>
        </div>

        <div className="stat-card clickable" onClick={() => navigate('/fleets')}>
          <div className="stat-value">{stats.fleets || 0}</div>
          <div className="stat-label">Flotily</div>
        </div>

        <div className="stat-card clickable" onClick={() => navigate('/fleet-drivers')}>
          <div className="stat-value">{stats.fleetDrivers || 0}</div>
          <div className="stat-label">Řidiči flotil</div>
        </div>

        <div className="stat-card clickable" onClick={() => navigate('/vehicles')}>
          <div className="stat-value">{stats.vehicles || 0}</div>
          <div className="stat-label">Vozidla</div>
        </div>
      </div>

      <div className="dashboard-grid-two">
        <div className="card dashboard-panel">
          <div className="card-header">
            <span className="card-title">Finance</span>
            <button className="dashboard-link-btn" onClick={() => navigate('/payouts')}>
              Výplaty
            </button>
          </div>

          <div className="dashboard-metric-list">
            <div className="dashboard-metric-row">
              <span>Tržby</span>
              <strong className="gold-text">{money(stats.revenue)}</strong>
            </div>
            <div className="dashboard-metric-row">
              <span>Peněženky řidičů</span>
              <strong>{money(stats.driverWalletBalance)}</strong>
            </div>
            <div className="dashboard-metric-row">
              <span>Peněženky flotil</span>
              <strong>{money(stats.fleetWalletBalance)}</strong>
            </div>
            <div className="dashboard-metric-row total">
              <span>Celkem k budoucím výplatám</span>
              <strong>{money(totalWallets)}</strong>
            </div>
          </div>
        </div>

        <div className="card dashboard-panel">
          <div className="card-header">
            <span className="card-title">Výplaty</span>
          </div>

          <div className="dashboard-metric-list">
            <div className="dashboard-metric-row">
              <span>Řidiči k vyplacení</span>
              <strong>{stats.pendingDriverPayouts || 0}</strong>
            </div>
            <div className="dashboard-metric-row">
              <span>Flotily k vyplacení</span>
              <strong>{stats.pendingFleetPayouts || 0}</strong>
            </div>
            <div className="dashboard-metric-row total">
              <span>Celkem subjektů</span>
              <strong>{pendingPayouts}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-section-title">Posledních 7 dní</div>

      <div className="charts-grid">
        <div className="chart-card">
          <div className="chart-title">Počet jízd</div>
          <div className="dashboard-chart-wrap">
            <canvas ref={chartRidesRef}></canvas>
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-title">Tržby (EUR)</div>
          <div className="dashboard-chart-wrap">
            <canvas ref={chartRevenueRef}></canvas>
          </div>
        </div>
      </div>
    </div>
  );
}
